import type { ReactElement } from 'react';

import { useRouter } from 'next/compat/router';
import { useCallback, useEffect, useState } from 'react';

import { S } from './styles';
import { useCartContext } from '../../../utilities/context/dynamic/CartContext';
import { useUserContext } from '../../../utilities/context/dynamic/UserContext';
import { useAppContext } from '../../../utilities/context/static/AppContext';
import { useStaticContext } from '../../../utilities/context/static/StaticContext';
import { useApplyVoucherMutation } from '../../../utilities/graphql/codegen';
import { fetchCartData } from '../../../utilities/graphql/queries';
import { useApolloContext } from '~/campaign/core/context/ApolloContext';

export const AppliedVoucherNotification = (): ReactElement => {
  const { notifications: { voucher: { saved = '', applied = '' } = {} } = {} } = useStaticContext();
  const router = useRouter();
  const { locale, siteType } = useAppContext();
  const { client: apolloClient } = useApolloContext();
  const { refetchUser } = useUserContext();
  const [message, setMessage] = useState('');
  const [isErrorMessage, setIsErrorMessage] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const { setMiniCart, setCartHovered } = useCartContext();
  const [applyVoucherMutation] = useApplyVoucherMutation();

  const removeUrlParam = useCallback(() => {
    const url = new URL(window.location.href);

    url.searchParams.delete('vouchercode');
    router?.replace(url, url, { shallow: true });
  }, [router]);

  useEffect(() => {
    const { vouchercode, utm_source: source = '' } = router?.query ?? {};

    const voucher = Array.isArray(vouchercode) ? vouchercode.at(0) : vouchercode;
    const utmSource = Array.isArray(source) ? source.at(0) : source;

    if (!voucher) {
      return;
    }

    (async () => {
      const { data } = await applyVoucherMutation({
        variables: {
          siteType,
          voucher,
          locale,
          utmSource,
        },
      });

      if (!data?.applyVoucher) {
        return;
      }

      const { cart, errors } = data.applyVoucher;

      // presence of absence of the properties acts like a "feature flag"
      const voucherApplied = !!cart && !errors;
      const voucherSaved = !cart && !errors;
      const voucherError = !cart && !!errors;

      switch (true) {
        case voucherApplied: {
          // voucher API cart data is broken so we need to refetch the cart manually after voucher was applied
          const { cart: updatedCart } = await fetchCartData({ siteType, locale, apolloClient });

          refetchUser();

          if (updatedCart) {
            setMiniCart(updatedCart);
            setMessage(applied);
            setShowMessage(true);
            setIsErrorMessage(false);
          }

          break;
        }
        case voucherSaved: {
          refetchUser();
          setMessage(saved);
          setShowMessage(true);
          setIsErrorMessage(false);

          break;
        }
        case voucherError: {
          setMessage(errors?.at(0)?.message as string);
          setShowMessage(true);
          setIsErrorMessage(true);

          break;
        }
        default: {
          // no action
        }
      }
    })();
  }, [
    applyVoucherMutation,
    locale,
    setMiniCart,
    router,
    setCartHovered,
    saved,
    applied,
    siteType,
    apolloClient,
    refetchUser,
  ]);

  return showMessage && message ? (
    <S.MessageWrapper>
      <S.Message
        $isError={isErrorMessage}
        data-testid={`global-notification-voucher-${isErrorMessage ? 'error' : 'success'}`}
      >
        {message}
      </S.Message>

      <S.CloseButton
        onClick={() => {
          removeUrlParam();
          setShowMessage(false);
        }}
        icon="close"
        variant="outline"
        colorway="dark"
        data-testid="global-notification-voucher-close"
      />
    </S.MessageWrapper>
  ) : (
    <></>
  );
};
